import { Component, EventEmitter, inject, Input, OnInit, ViewEncapsulation } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import { Router } from '@angular/router';
import { Session } from '@models/session';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { OverlayService } from '@services/overlay';
import { RetakeDocumentPayload } from 'src/app/interfaces/retake-document-payload.interface';
import { CommonModule } from '@angular/common';

interface ValidationResponse {
  facePhotoReason?: boolean;
  idPhotoReason?: boolean;
}

interface RetakeDocumentForm {
  facePhoto: FormControl<boolean>;
  idPhoto: FormControl<boolean>;
  facePhotoReason: FormControl<string | null>;
  idPhotoReason: FormControl<string | null>;
}

@Component({
  selector: 'lv-agent-retake-document',
  templateUrl: './agent-retake-document.component.html',
  styleUrls: ['./agent-retake-document.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule]
})
export class AgentRetakeDocumentComponent implements OnInit {
  private formBuilder = inject(FormBuilder);
  private overlayService = inject(OverlayService);
  private router = inject(Router);
  private translateService = inject(TranslateService);

  @Input() session: Session;
  @Input() submitted: EventEmitter<RetakeDocumentPayload>;

  form!: FormGroup<RetakeDocumentForm>;

  ngOnInit() {
    this.form = this.formBuilder.group<RetakeDocumentForm>(
      {
        facePhoto: new FormControl<boolean>(false, { validators: Validators.required }),
        idPhoto: new FormControl<boolean>(false, { validators: Validators.required }),
        facePhotoReason: new FormControl<string | null>(null),
        idPhotoReason: new FormControl<string | null>(null)
      },
      { validators: this.reasonsValidator }
    );
  }

  get learner() {
    return this.session.learner;
  }

  labelForItem(item: string) {
    return this.translateService.instant(`agent-reject-document.form.item-options.${item}`);
  }

  onSubmit() {
    this.submitted.emit(<RetakeDocumentPayload>this.form.value);
    this.overlayService.closeTopOverlay();
    this.router.navigateByUrl('/');
  }

  onCancel() {
    this.overlayService.closeTopOverlay();
  }

  private reasonsValidator(control: AbstractControl): ValidationErrors | null {
    const facePhoto = control.get('facePhoto').value;
    const idPhoto = control.get('idPhoto').value;

    // If you are rejecting a document, one of them should be invalid.
    if (!facePhoto && !idPhoto) {
      return { facePhotoReason: false, idPhotoReason: false };
    }

    const facePhotoReason = control.get('facePhotoReason').value;
    const idPhotoReason = control.get('idPhotoReason').value;

    const response: ValidationResponse = {};

    // If face photo is invalid, you shall provide a reason.
    if (facePhoto && !facePhotoReason) {
      response.facePhotoReason = true;
    }

    // If ID photo is invalid, you shall provide a reason.
    if (idPhoto && !idPhotoReason) {
      response.idPhotoReason = true;
    }

    return response;
  }
}
